import React, { Fragment, useContext } from "react";
import { makeStyles } from "tss-react/mui";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import { GlobalsContext } from "../../../context/globals";
import logo_blanco_color from "../../../images/logo_blanco_color.png";
import logo_negro from "../../../images/logo_negro.png";
import ConectionAlert from "../../conectionAlert";
import packageJson from "../../../../package.json";
import fondo_dark from "../../../images/fondo_dark.png";
import fondo_light from "../../../images/fondo_light.png";
import fondo_mid from "../../../images/fondo_mid.png";

const useStyles = makeStyles()((theme) => {
  const espaciado = theme.spacing(4); // 32px
  return {
    container: {
      // Ocupa el alto de la ventana: el scroll queda en la columna derecha, no en body
      minHeight: "100dvh",
      height: "100dvh",
      maxHeight: "100dvh",
      overflow: "hidden",
      alignItems: "stretch",
      backgroundColor: "#00203a",
    },
    leftContainer: {
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      display: "none",
      [theme.breakpoints.up("md")]: {
        display: "flex",
      },
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: espaciado,
      overflow: "hidden",
      boxSizing: "border-box",
      backgroundSize: "cover",
      backgroundPosition: "left",
      "& img": {
        width: "100%",
        maxWidth: 720,
        height: "auto",
        objectFit: "contain",
      },
    },
    rightContainer: {
      position: "relative",
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      display: "flex",
      flexDirection: "column",
      padding: espaciado,
      paddingTop: 0,
      overflow: "hidden",
      boxSizing: "border-box",
    },
    themeBar: {
      flex: "0 0 auto",
      display: "flex",
      justifyContent: "flex-end",
      width: "100%",
      maxWidth: "100%",
      paddingTop: espaciado,
      paddingLeft: 0,
      paddingRight: 0,
      boxSizing: "border-box",
    },
    rightColumnScroll: {
      flex: "1 1 0",
      minHeight: 0,
      overflowX: "hidden",
      overflowY: "auto",
      // Evita que al llegar al final el scroll “salte” al body
      overscrollBehavior: "contain",
      WebkitOverflowScrolling: "touch",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      boxSizing: "border-box",
    },
    rightColumnScrollInner: {
      width: "100%",
      maxWidth: 560,
      flex: "0 0 auto",
      // Con padding sobrante, reparte arriba y abajo (centra el bloque en la columna)
      marginTop: "auto",
      marginBottom: "auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      boxSizing: "border-box",
      // El logo no crece con el ancho del formulario
      "& > img": {
        maxWidth: 300,
        width: "100%",
        height: "auto",
        objectFit: "contain",
        alignSelf: "center",
      },
      "& h1": {
        textAlign: "center",
        margin: 0,
        marginTop: theme.spacing(1.5),
      },
    },
    inputContainer: {
      padding: espaciado,
      width: "100%",
    },
    themeToggleWrap: {
      // Filas fija encima del área con scroll (misma columna, sin empujar el cuerpo)
      zIndex: 1,
      width: "auto",
      maxWidth: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    themeToggle: {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        transform: "scale(1.05)",
      },
      "&.Mui-focusVisible": {
        backgroundColor: "rgba(255, 255, 255, 0.24)",
      },
      transition: "background-color 0.2s ease, transform 0.2s ease",
    },
    themeToggleLight: {
      backgroundColor: "rgba(0, 0, 0, 0.06)",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.12)",
      },
      "&.Mui-focusVisible": {
        backgroundColor: "rgba(0, 0, 0, 0.14)",
      },
    },
  };
});

function Layout(props) {
  const { classes } = useStyles();
  const theme = useTheme();
  const prefersDarkMode = theme.palette.mode === "dark";
  const { darkModeOn, setDarkModeOn } = useContext(GlobalsContext);

  const handleToggleTheme = () => setDarkModeOn(!darkModeOn);

  return (
    <Fragment>
      <CssBaseline />
      <Grid
        container
        className={classes.container}
        direction="row"
        justifyContent="center"
        wrap="nowrap"
      >
        <Grid
          size={{ xs: 12, md: 6 }}
          className={classes.leftContainer}
          style={{
            backgroundImage: "url(" + fondo_dark + ")",
            backgroundSize: "cover",
            backgroundPosition: "left",
          }}
        >
          <img src={logo_blanco_color} alt="logo" />
        </Grid>
        <Grid
          size={{ xs: 12, md: 6 }}
          className={classes.rightContainer}
          style={{
            backgroundImage: "url(" + (prefersDarkMode ? fondo_mid : fondo_light) + ")",
            backgroundSize: "cover",
            backgroundPosition: "right",
          }}
        >
          <Box className={classes.themeBar}>
            <Box className={classes.themeToggleWrap} data-layout="theme-toggle">
              <Tooltip title={darkModeOn ? "Usar modo claro" : "Usar modo oscuro"} arrow placement="left">
                <IconButton
                  onClick={handleToggleTheme}
                  className={prefersDarkMode ? classes.themeToggle : `${classes.themeToggle} ${classes.themeToggleLight}`}
                  size="medium"
                  aria-label={darkModeOn ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
                >
                  {prefersDarkMode ? (
                    <LightModeRoundedIcon sx={{ fontSize: 26 }} />
                  ) : (
                    <DarkModeRoundedIcon sx={{ fontSize: 26 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box className={classes.rightColumnScroll}>
            <Box className={classes.rightColumnScrollInner}>
              <img
                src={prefersDarkMode ? logo_blanco_color : logo_negro}
                alt="logo"
              />
              <h1>Laboratorio Central</h1>
              <Fade in={true} timeout={1000}>
                <Paper className={classes.inputContainer}>{props.children}</Paper>
              </Fade>
              <Box pt={1}>
                <Typography variant="body2" color="textSecondary" align="center">
                  {"Laboratorio Central - " + new Date().getFullYear()}
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  versión {packageJson.version}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <ConectionAlert />
    </Fragment>
  );
}

export default Layout;
